import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { 
  Shield,
  MapPin,
  Clock,
  Users,
  Plus,
  Trash2,
  Play,
  Square,
  AlertTriangle,
  Phone,
  Mail,
  Navigation,
  Share2,
  Copy,
  History,
  CheckCircle,
  XCircle,
  Loader2,
  Locate,
  Signal,
  Map
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker for current position
const currentPositionIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FF3366" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#FF3366"/>
      <circle cx="12" cy="12" r="6" fill="white"/>
      <circle cx="12" cy="12" r="3" fill="#FF3366"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Component to recenter map
const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);
  return null;
};

const SafeCheckPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [activeTrip, setActiveTrip] = useState(null);
  const [history, setHistory] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showStartTrip, setShowStartTrip] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [locationHistory, setLocationHistory] = useState([]);
  const watchIdRef = useRef(null);
  const updateIntervalRef = useRef(null);
  
  // New contact form
  const [newContact, setNewContact] = useState({ name: "", phone: "", email: "" });
  
  // New trip form
  const [tripData, setTripData] = useState({
    destination: "",
    estimated_duration: 30,
    contacts: [],
    message: ""
  });

  // Get current position - defined before useEffect that uses it
  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError("Géolocalisation non supportée par votre navigateur");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setCurrentPosition(pos);
        setLocationError(null);
      },
      (error) => {
        setLocationError(getLocationErrorMessage(error));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      try {
        const [contactsRes, activeTripRes, historyRes] = await Promise.all([
          axios.get(`${API}/user/emergency-contacts`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/safecheck/active`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null })),
          axios.get(`${API}/safecheck/history`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }))
        ]);
        
        setContacts(contactsRes.data || []);
        setActiveTrip(activeTripRes.data);
        if (activeTripRes.data?.locations) {
          setLocationHistory(activeTripRes.data.locations.map(l => ({ lat: l.lat, lng: l.lng })));
        }
        setHistory(historyRes.data || []);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    
    loadData();
    getCurrentPosition();
    
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
    };
  }, [token, getCurrentPosition]);

  // Start tracking when active trip exists
  useEffect(() => {
    if (activeTrip && activeTrip.status === "active" && !isTracking) {
      if (!navigator.geolocation) return;
      
      setIsTracking(true);
      
      // Watch position continuously
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          };
          setCurrentPosition(pos);
          setLocationHistory(prev => [...prev.slice(-50), pos]);
          setLocationError(null);
        },
        (error) => {
          setLocationError(getLocationErrorMessage(error));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
      );

      // Send position updates to server every 30 seconds
      updateIntervalRef.current = setInterval(() => {
        if (watchIdRef.current !== null) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              try {
                await axios.post(`${API}/safecheck/${activeTrip.id}/update`, 
                  { 
                    status: "active",
                    latitude: pos.lat,
                    longitude: pos.lng
                  },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              } catch (error) {
                console.error("Error sending location:", error);
              }
            },
            () => {},
            { enableHighAccuracy: true, timeout: 5000 }
          );
        }
      }, 30000);
    } else if (!activeTrip || activeTrip.status !== "active") {
      // Stop tracking
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
      setIsTracking(false);
    }
    
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
    };
  }, [activeTrip, token, isTracking]);

  // Helper functions moved out of effects
  const stopTrackingNow = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
    setIsTracking(false);
  };

  const getLocationErrorMessage = (error) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return "Accès à la localisation refusé. Autorisez l'accès dans les paramètres.";
      case error.POSITION_UNAVAILABLE:
        return "Position indisponible. Vérifiez votre GPS.";
      case error.TIMEOUT:
        return "Délai d'attente dépassé. Réessayez.";
      default:
        return "Erreur de localisation.";
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/user/emergency-contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchActiveTrip = async () => {
    try {
      const response = await axios.get(`${API}/safecheck/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveTrip(response.data);
      if (response.data?.locations) {
        setLocationHistory(response.data.locations.map(l => ({ lat: l.lat, lng: l.lng })));
      }
    } catch (error) {
      console.error("Error fetching active trip:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/safecheck/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const addContact = async () => {
    if (!newContact.name || !newContact.phone) {
      toast.error("Nom et téléphone requis");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/user/emergency-contacts`, newContact, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contact ajouté !");
      setNewContact({ name: "", phone: "", email: "" });
      setShowAddContact(false);
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/user/emergency-contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contact supprimé");
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const startTrip = async () => {
    if (!tripData.destination || tripData.contacts.length === 0) {
      toast.error("Destination et au moins un contact requis");
      return;
    }

    // Send current position with trip start
    if (!currentPosition) {
      toast.error("Position non disponible. Activez la géolocalisation.");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/safecheck/start`, {
        ...tripData,
        latitude: currentPosition.lat,
        longitude: currentPosition.lng
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Trajet démarré ! Vos contacts ont été notifiés.");
      setActiveTrip(response.data);
      setShowStartTrip(false);
      setTripData({ destination: "", estimated_duration: 30, contacts: [], message: "" });
      setLocationHistory([currentPosition]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const endTrip = async (status = "completed") => {
    if (!activeTrip) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/safecheck/${activeTrip.id}/update`, 
        { 
          status,
          latitude: currentPosition?.lat,
          longitude: currentPosition?.lng
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (status === "completed") {
        toast.success("Trajet terminé ! Bien arrivé(e) !");
      } else if (status === "sos") {
        toast.error("Alerte SOS envoyée à vos contacts !");
      }
      
      stopTrackingNow();
      setActiveTrip(null);
      setLocationHistory([]);
      
      // Refresh history
      try {
        const historyRes = await axios.get(`${API}/safecheck/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(historyRes.data || []);
      } catch (histErr) {
        console.error("Error fetching history:", histErr);
      }
    } catch (error) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  const toggleContactSelection = (contactId) => {
    setTripData(prev => ({
      ...prev,
      contacts: prev.contacts.includes(contactId)
        ? prev.contacts.filter(id => id !== contactId)
        : [...prev.contacts, contactId]
    }));
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/track/${activeTrip?.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Lien copié !");
  };

  const getTimeRemaining = () => {
    if (!activeTrip?.estimated_end) return null;
    const end = new Date(activeTrip.estimated_end);
    const now = new Date();
    const diff = end - now;
    if (diff <= 0) return "Temps écoulé";
    const minutes = Math.floor(diff / 60000);
    return `${minutes} min restantes`;
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3366]/10 text-[#FF3366] text-sm font-medium mb-3">
            <Shield size={16} />
            Sécurité
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Safe Check
          </h1>
          <p className="text-slate-500 mt-1">Partagez votre trajet en temps réel</p>
        </div>

        {/* Privacy Notice */}
        <Card className="guardian-card bg-blue-50/50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Shield size={16} className="text-blue-600" />
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Protection de vos données</p>
                <p className="text-blue-600 leading-relaxed">
                  Le partage de position en temps réel avec votre cercle de confiance est optionnel. 
                  Vous pouvez l'activer et le désactiver à tout moment. 
                  Les données de localisation ne sont pas conservées au-delà de 24h.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Status */}
        <Card className={`guardian-card ${locationError ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {locationError ? (
                  <AlertTriangle className="text-amber-600" size={20} />
                ) : (
                  <Locate className={`text-green-600 ${isTracking ? 'animate-pulse' : ''}`} size={20} />
                )}
                <div>
                  <p className={`font-medium ${locationError ? 'text-amber-800' : 'text-green-800'}`}>
                    {locationError 
                      ? "Localisation indisponible" 
                      : (isTracking ? "Position en direct" : "Position disponible")}
                  </p>
                  {currentPosition && !locationError && (
                    <p className="text-xs text-green-600">
                      {currentPosition.lat.toFixed(5)}, {currentPosition.lng.toFixed(5)}
                      {currentPosition.accuracy && ` (±${Math.round(currentPosition.accuracy)}m)`}
                    </p>
                  )}
                  {locationError && <p className="text-xs text-amber-600">{locationError}</p>}
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={getCurrentPosition}
                className="rounded-full"
              >
                <Locate size={14} className="mr-1" />
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active Trip with Map */}
        {activeTrip && (
          <Card className="border-2 border-green-500 bg-green-50 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Navigation className="animate-pulse" size={20} />
                Trajet en cours
                {isTracking && (
                  <Badge className="bg-green-600 text-white ml-2">
                    <Signal size={12} className="mr-1" />
                    En direct
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Live Map */}
              {currentPosition && (
                <div className="h-48 rounded-xl overflow-hidden border-2 border-green-200">
                  <MapContainer 
                    center={[currentPosition.lat, currentPosition.lng]} 
                    zoom={15} 
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={false}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; OpenStreetMap'
                    />
                    <RecenterMap position={[currentPosition.lat, currentPosition.lng]} />
                    
                    {/* Path history */}
                    {locationHistory.length > 1 && (
                      <Polyline 
                        positions={locationHistory.map(p => [p.lat, p.lng])}
                        color="#FF3366"
                        weight={4}
                        opacity={0.7}
                      />
                    )}
                    
                    {/* Current position marker */}
                    <Marker 
                      position={[currentPosition.lat, currentPosition.lng]}
                      icon={currentPositionIcon}
                    >
                      <Popup>
                        <strong>{user?.full_name}</strong><br/>
                        Position actuelle
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-green-600" />
                <span className="font-medium">{activeTrip.destination}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-green-600" />
                <span>{getTimeRemaining()}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Users size={16} className="text-green-600" />
                <span>{activeTrip.contacts?.length || 0} contact(s) notifié(s)</span>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl"
                  onClick={copyShareLink}
                >
                  <Copy size={16} className="mr-2" />
                  Copier le lien
                </Button>
                <Button 
                  className="flex-1 btn-guardian rounded-xl"
                  onClick={() => endTrip("completed")}
                  disabled={loading}
                >
                  <CheckCircle size={16} className="mr-2" />
                  Bien arrivé(e)
                </Button>
              </div>
              
              <Button 
                variant="destructive"
                className="w-full rounded-xl"
                onClick={() => endTrip("sos")}
                disabled={loading}
              >
                <AlertTriangle size={16} className="mr-2" />
                ALERTE SOS
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Start New Trip */}
        {!activeTrip && (
          <>
            {showStartTrip ? (
              <Card className="guardian-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Navigation size={20} className="text-[#FF3366]" />
                    Nouveau trajet
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Mini map preview */}
                  {currentPosition && (
                    <div className="h-32 rounded-xl overflow-hidden border">
                      <MapContainer 
                        center={[currentPosition.lat, currentPosition.lng]} 
                        zoom={14} 
                        style={{ height: '100%', width: '100%' }}
                        zoomControl={false}
                        dragging={false}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[currentPosition.lat, currentPosition.lng]} icon={currentPositionIcon} />
                      </MapContainer>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label className="font-bold">Destination</Label>
                    <Input
                      placeholder="Ex: 15 Rue de la Paix, Lyon"
                      value={tripData.destination}
                      onChange={e => setTripData({...tripData, destination: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-bold">Durée estimée (minutes)</Label>
                    <Input
                      type="number"
                      min="5"
                      max="480"
                      value={tripData.estimated_duration}
                      onChange={e => setTripData({...tripData, estimated_duration: parseInt(e.target.value) || 30})}
                      className="rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-bold">Contacts à notifier</Label>
                    {contacts.length === 0 ? (
                      <p className="text-sm text-slate-500">Aucun contact. Ajoutez-en d'abord !</p>
                    ) : (
                      <div className="space-y-2">
                        {contacts.map(contact => (
                          <div 
                            key={contact.id}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                              tripData.contacts.includes(contact.id) 
                                ? "border-[#FF3366] bg-[#FF3366]/5" 
                                : "border-slate-200"
                            }`}
                            onClick={() => toggleContactSelection(contact.id)}
                          >
                            <Checkbox 
                              checked={tripData.contacts.includes(contact.id)}
                              className="data-[state=checked]:bg-[#FF3366] data-[state=checked]:border-[#FF3366]"
                            />
                            <div className="flex-1">
                              <p className="font-medium">{contact.name}</p>
                              <p className="text-sm text-slate-500">{contact.phone}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-bold">Message (optionnel)</Label>
                    <Textarea
                      placeholder="Ex: Je rentre du travail"
                      value={tripData.message}
                      onChange={e => setTripData({...tripData, message: e.target.value})}
                      className="rounded-xl resize-none"
                      rows={2}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 rounded-xl"
                      onClick={() => setShowStartTrip(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="flex-1 btn-guardian rounded-xl"
                      onClick={startTrip}
                      disabled={loading || contacts.length === 0 || !currentPosition}
                    >
                      {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Play size={16} className="mr-2" />}
                      Démarrer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card 
                className="guardian-card cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setShowStartTrip(true)}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-[#FF3366]/10 flex items-center justify-center mx-auto mb-4">
                    <Navigation size={36} className="text-[#FF3366]" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Démarrer un trajet</h3>
                  <p className="text-slate-500">Vos contacts d'urgence pourront suivre votre position en temps réel</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Emergency Contacts */}
        <Card className="guardian-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users size={20} className="text-[#FF3366]" />
                Contacts d'urgence
              </span>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() => setShowAddContact(!showAddContact)}
              >
                <Plus size={16} className="mr-1" />
                Ajouter
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {showAddContact && (
              <Card className="bg-slate-50 border-dashed">
                <CardContent className="p-4 space-y-3">
                  <Input
                    placeholder="Nom"
                    value={newContact.name}
                    onChange={e => setNewContact({...newContact, name: e.target.value})}
                    className="rounded-xl"
                  />
                  <Input
                    placeholder="Téléphone"
                    value={newContact.phone}
                    onChange={e => setNewContact({...newContact, phone: e.target.value})}
                    className="rounded-xl"
                  />
                  <Input
                    placeholder="Email (optionnel)"
                    type="email"
                    value={newContact.email}
                    onChange={e => setNewContact({...newContact, email: e.target.value})}
                    className="rounded-xl"
                  />
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 rounded-xl"
                      onClick={() => setShowAddContact(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="flex-1 btn-guardian rounded-xl"
                      onClick={addContact}
                      disabled={loading}
                    >
                      Ajouter
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {contacts.length === 0 ? (
              <p className="text-center text-slate-500 py-4">
                Aucun contact d'urgence. Ajoutez-en pour utiliser Safe Check.
              </p>
            ) : (
              contacts.map(contact => (
                <div 
                  key={contact.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div>
                    <p className="font-medium text-slate-900">{contact.name}</p>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Phone size={12} />
                        {contact.phone}
                      </span>
                      {contact.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={12} />
                          {contact.email}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => deleteContact(contact.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* History */}
        {history.length > 0 && (
          <Card className="guardian-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History size={20} className="text-[#FF3366]" />
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {history.slice(0, 5).map(trip => (
                <div 
                  key={trip.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                >
                  <div>
                    <p className="font-medium text-slate-900">{trip.destination}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(trip.started_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <Badge className={
                    trip.status === "completed" 
                      ? "bg-green-100 text-green-700" 
                      : (trip.status === "sos" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700")
                  }>
                    {trip.status === "completed" 
                      ? "Terminé" 
                      : (trip.status === "sos" ? "SOS" : trip.status)}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SafeCheckPage;
